import { useState, useEffect } from "react";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// Types matching backend schemas
type ProviderName = "openai" | "anthropic" | "google" | "azure_openai";

export interface LLMProviderFormData {
  provider_name: ProviderName;
  display_name: string;
  // Opcional al EDITAR: si no se envía, el backend conserva la key actual.
  // (Enviar "" NO sirve: el backend cifraría la cadena vacía — lo bloquea
  // la validación min_length=1 con un 422.)
  api_key?: string;
  api_base_url?: string;
  default_model: string;
  temperature_default: number;
  rate_limit_rpm: number;
  monthly_budget_usd?: number;
  priority: number;
  is_active: boolean;
}

interface LLMProviderFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: LLMProviderFormData) => Promise<void>;
  initialData?: Partial<LLMProviderFormData>;
  mode?: "create" | "edit";
}

// Fallback local si la API del catálogo no responde o viene vacía.
// La fuente de verdad es GET /api/v1/llm/models (backend/config/llm_models.yaml):
// este listado solo evita un desplegable vacío si esa llamada falla.
// OJO: los IDs de Google llevan el prefijo "gemini/" (requisito de LiteLLM
// para enrutar a AI Studio con API key; sin él intenta Vertex AI y falla).
const PROVIDER_MODELS: Record<ProviderName, { value: string; label: string }[]> = {
  openai: [
    { value: "gpt-4o", label: "GPT-4o (Recomendado)" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  ],
  anthropic: [
    { value: "claude-sonnet-5", label: "Claude Sonnet 5 (Recomendado)" },
    { value: "claude-opus-4-8", label: "Claude Opus 4.8" },
    { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
  ],
  google: [
    { value: "gemini/gemini-3.5-flash", label: "Gemini 3.5 Flash (Recomendado)" },
    { value: "gemini/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (Preview)" },
    { value: "gemini/gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  ],
  azure_openai: [
    { value: "gpt-4o", label: "GPT-4o (Deployment)" },
    { value: "gpt-4-turbo", label: "GPT-4 Turbo (Deployment)" },
  ],
};

interface CatalogModel {
  id: string;
  name: string;
  context_window: number;
}

const PROVIDER_LABELS: Record<ProviderName, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  azure_openai: "Azure OpenAI",
};

export function LLMProviderForm({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  mode = "create",
}: LLMProviderFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  // Catálogo de modelos servido por el backend (config/llm_models.yaml).
  // null = aún no cargado / falló → se usa el fallback local PROVIDER_MODELS.
  const [catalogModels, setCatalogModels] = useState<Record<
    string,
    { value: string; label: string }[]
  > | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const resp: { models?: Record<string, CatalogModel[]> } =
          await fetchWithAuth("/api/v1/llm/models");
        const byProvider: Record<string, { value: string; label: string }[]> = {};
        for (const [prov, models] of Object.entries(resp.models ?? {})) {
          byProvider[prov] = models.map((m) => ({ value: m.id, label: m.name }));
        }
        if (!cancelled) setCatalogModels(byProvider);
      } catch {
        // Sin catálogo remoto: el fallback local sigue funcionando.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const modelsFor = (provider: ProviderName): { value: string; label: string }[] => {
    const fromCatalog = catalogModels?.[provider];
    return fromCatalog && fromCatalog.length > 0 ? fromCatalog : PROVIDER_MODELS[provider];
  };
  const [formData, setFormData] = useState<LLMProviderFormData>({
    provider_name: "openai",
    display_name: "",
    api_key: "",
    api_base_url: "",
    default_model: "gpt-4o",
    temperature_default: 0.7,
    rate_limit_rpm: 60,
    monthly_budget_usd: undefined,
    priority: 5,
    is_active: true,
    ...initialData,
  });

  // Resetear formulario cuando cambia initialData
  useEffect(() => {
    if (initialData) {
      setFormData({
        provider_name: "openai",
        display_name: "",
        api_key: "",
        api_base_url: "",
        default_model: "gpt-4o",
        temperature_default: 0.7,
        rate_limit_rpm: 60,
        monthly_budget_usd: undefined,
        priority: 5,
        is_active: true,
        ...initialData,
      });
    }
  }, [initialData]);

  // Auto-generar display_name si está vacío
  useEffect(() => {
    if (!formData.display_name && mode === "create") {
      setFormData((prev) => ({
        ...prev,
        display_name: PROVIDER_LABELS[prev.provider_name],
      }));
    }
  }, [formData.provider_name, mode]);

  const handleProviderChange = (provider: ProviderName) => {
    const firstModel = modelsFor(provider)[0].value;
    setFormData((prev) => ({
      ...prev,
      provider_name: provider,
      default_model: firstModel,
      display_name: PROVIDER_LABELS[provider],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!formData.display_name.trim()) {
      alert("El nombre de visualización es requerido");
      return;
    }

    if (!formData.api_key?.trim() && mode === "create") {
      alert("La API Key es requerida");
      return;
    }

    if (formData.provider_name === "azure_openai" && !formData.api_base_url?.trim()) {
      alert("La URL base es requerida para Azure OpenAI");
      return;
    }

    try {
      setIsSubmitting(true);

      // Preparar datos (omitimos api_key cuando estamos editando y no se ha cambiado)
      const { api_key, ...rest } = formData;
      const submitData: LLMProviderFormData = {
        ...rest,
        api_key,
        api_base_url:
          formData.api_base_url?.trim() || formData.provider_name === "azure_openai"
            ? formData.api_base_url
            : undefined,
        monthly_budget_usd:
          formData.monthly_budget_usd && formData.monthly_budget_usd > 0
            ? formData.monthly_budget_usd
            : undefined,
      };

      if (mode === "edit" && !api_key?.trim()) {
        // En modo edición sin api_key nueva NO se envía el campo: el backend
        // conserva la actual solo si está AUSENTE del payload. (El antiguo
        // envío de "" provocaba un 422 de validación — y de haber pasado,
        // habría sobrescrito la key real con la cadena vacía cifrada.)
        delete submitData.api_key;
      }

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast.error(error instanceof Error ? error.message : "Error al guardar proveedor");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-800">
            {mode === "create" ? "Nuevo Proveedor LLM" : "Editar Proveedor LLM"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Proveedor <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.provider_name}
              onChange={(e) => handleProviderChange(e.target.value as ProviderName)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={mode === "edit"}
            >
              {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {mode === "edit" && (
              <p className="text-xs text-gray-500 mt-1">
                El proveedor no puede ser modificado después de crearlo
              </p>
            )}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de visualización <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="Ej: OpenAI GPT-4o Producción"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key {mode === "create" && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <input
                type={showApiKey ? "text" : "password"}
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                placeholder={mode === "edit" ? "Dejar vacío para mantener la actual" : "sk-..."}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                required={mode === "create"}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* API Base URL (solo para Azure) */}
          {formData.provider_name === "azure_openai" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL Base <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.api_base_url || ""}
                onChange={(e) => setFormData({ ...formData, api_base_url: e.target.value })}
                placeholder="https://<resource-name>.openai.azure.com/"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                required={formData.provider_name === "azure_openai"}
              />
              <p className="text-xs text-gray-500 mt-1">
                Endpoint de tu recurso Azure OpenAI
              </p>
            </div>
          )}

          {/* Default Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modelo por defecto <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.default_model}
              onChange={(e) => setFormData({ ...formData, default_model: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {/* Si el modelo actual (p. ej. al editar) no está en el catálogo,
                  se añade como opción para que el select refleje el valor real. */}
              {!modelsFor(formData.provider_name).some(
                (m) => m.value === formData.default_model
              ) &&
                formData.default_model && (
                  <option value={formData.default_model}>{formData.default_model}</option>
                )}
              {modelsFor(formData.provider_name).map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperatura por defecto: {Number(formData.temperature_default).toFixed(1)}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={formData.temperature_default}
              onChange={(e) =>
                setFormData({ ...formData, temperature_default: parseFloat(e.target.value) })
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Más preciso</span>
              <span>Más creativo</span>
            </div>
          </div>

          {/* Rate Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rate Limit (requests/min)
            </label>
            <input
              type="number"
              min="1"
              max="10000"
              value={formData.rate_limit_rpm}
              onChange={(e) =>
                setFormData({ ...formData, rate_limit_rpm: parseInt(e.target.value) || 60 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Monthly Budget */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Presupuesto mensual (USD) - Opcional
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.monthly_budget_usd || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  monthly_budget_usd: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              placeholder="Ej: 100.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Deja vacío para no limitar el gasto mensual
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prioridad de fallback: {formData.priority}
            </label>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1 = Máxima prioridad</span>
              <span>10 = Mínima prioridad</span>
            </div>
          </div>

          {/* Active Switch */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Proveedor activo</label>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.is_active ? "bg-blue-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.is_active ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Guardando...
                </>
              ) : (
                <>Guardar y probar conexión</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
